// src/components/UpgradeMembershipModal.js
// ============================================================================
// IN-PLACE MEMBERSHIP UPGRADE MODAL (founder 2026-07-13)
//
// Shown when a non-member hits a members-only action (event creation). The
// whole funnel happens INSIDE the modal — plan pick -> embedded Stripe
// checkout -> success — with zero navigation away from the screen they're on.
// Navigating to the Subscription screen was rejected as an abandoned-cart
// risk: the user's filled-out form stays visible behind the scrim the whole
// time, so completing checkout drops them right back on their almost-created
// event.
//
// Checkout mechanics mirror SubscriptionScreen exactly (createSubscription-
// Checkout -> clientSecret -> stripe.createEmbeddedCheckoutPage into a raw
// DOM div; hosted-URL fallback on native), including the post-webhook poll
// via getMySubscription.
//
// Renewal consent (evergreen 3.5a, design 13.7 / 14.1): a plan tap opens a
// 'confirm' step (the renewal disclosure + an UNTICKED consent box) and only
// "Continue to payment" calls createSubscriptionCheckout, carrying the
// consent. The Plan disclosure and consent templates below are shared with
// SubscriptionScreen (and mirror billingNotices.js planDisclosure /
// planConsentText on the server).
// ============================================================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  View, Text, Modal, Pressable, ScrollView, ActivityIndicator, StyleSheet, Platform,
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { THEME } from '../constants/uiTheme';
import useCritsEnabled from '../hooks/useCritsEnabled';
import ConsentCheckbox, { ConsentHint } from './ConsentCheckbox';

const IS_WEB = Platform.OS === 'web';
const C = THEME.colors; // brand #FCD000, surface #111111, borders #222/#333

// Display mirror of functions/modules/subscriptions.js SUBSCRIPTION_TIERS —
// keep in sync if pricing changes (single source of truth is the backend;
// checkout always charges the server-side price regardless of what we show).
// priceCents is what the renewal consent names; a stale mirror is caught by
// the server (plan-price-changed) and the confirm step re-renders with the
// server's price, unticked.
const TIERS = [
  { key: 'rookie', name: 'Rookie', priceCents: 1500, crits: 40 },
  { key: 'pro', name: 'Pro', priceCents: 3000, crits: 100, popular: true },
  { key: 'elite', name: 'Elite', priceCents: 5000, crits: 250 },
];

// ---- Plan renewal disclosure + consent (3.5a, design 13.7 / 14.1) ----------
// Exported for SubscriptionScreen (these live here only because the 3.5a
// units are file-disjoint). Same templates as billingNotices.js
// planDisclosure / planConsentText; the C7 harness checks every tier and
// interval against them.

// "$15.00" (billingNotices formatUsd).
export const formatPlanUsd = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

// "October 26, 2026" in the house email zone (billingNotices billingDate).
export const formatPlanDate = (ms) => new Date(ms).toLocaleDateString('en-US', {
  month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago',
});

const planIntervalWord = (interval) => (interval === 'year' ? 'year' : 'month');

// The renewal terms shown before a Plan purchase or change. Checkout adds the
// saved-card sentence (14.1); a change (`change: true`) shows the
// two-sentence form of 13.7, prefixed "Starting {date}, " when the new term
// starts at the next billing date (`startsAtMs`, always a change;
// `startsAtNextBilling` is the client-only wording when that date is unknown).
export function planDisclosure(tierName, cents, interval, { startsAtMs = null, startsAtNextBilling = false, change = false } = {}) {
  const every = `every ${planIntervalWord(interval)}${Number.isInteger(cents) && cents > 0 ? ` at ${formatPlanUsd(cents)}` : ''}`;
  const cancel = 'Cancel anytime on the Plans page; your plan stays until the end of the period you paid for.';
  if (Number.isFinite(startsAtMs) && startsAtMs > 0) return `Starting ${formatPlanDate(startsAtMs)}, renews ${every} until you cancel. ${cancel}`;
  if (startsAtNextBilling) return `Starting at your next billing date, renews ${every} until you cancel. ${cancel}`;
  if (change) return `Renews ${every} until you cancel. ${cancel}`;
  return `Renews ${every} until you cancel. ${cancel} Your card is saved for renewals and for purchases you confirm later.`;
}

// The express renewal consent next to the box (server: planConsentText).
export const planConsentText = (interval, cents) =>
  `I agree that my plan renews automatically every ${planIntervalWord(interval)} at ${formatPlanUsd(cents)} until I cancel on the Plans page.`;

// The server's sentence without its "token: " prefix (house style,
// rumblePayment.js membershipErrorMessage).
export const planErrorSentence = (err, fallback = 'Please try again.') =>
  String(err?.message || '').replace(/^[a-z][a-z-]*:\s*/, '') || fallback;

// A `plan-price-changed:` refusal -> the server's {amountCents, interval}, else null.
export function planPriceChangedDetails(err) {
  if (!String(err?.message || '').includes('plan-price-changed')) return null;
  const d = err?.details || {};
  const amountCents = Number(d.amountCents);
  const interval = d.interval === 'year' ? 'year' : d.interval === 'month' ? 'month' : null;
  return Number.isInteger(amountCents) && amountCents > 0 && interval ? { amountCents, interval } : null;
}

export const RENEWAL_CONSENT_REFRESH = 'Please refresh the page to review the renewal terms, then try again.';

export default function UpgradeMembershipModal({
  visible,
  onClose,
  onUpgraded,       // called when the user taps Continue after a successful checkout
  onSeeAllPlans,    // optional: link out to the full Subscription screen (yearly plans etc.)
  headline = 'Create Your Own Events',
  subtitle = 'Events above 16 are included with paid plans. Pick a plan — your event is one checkout away.',
}) {
  const critsEnabled = useCritsEnabled(); // settings/economy.critsEnabled — Crit lines only
  const [step, setStep] = useState('plans'); // 'plans' | 'confirm' | 'checkout' | 'success'
  const [busyTier, setBusyTier] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [embedded, setEmbedded] = useState(null); // { clientSecret, publishableKey, disclosure }
  // The plan being confirmed: { key, name, amountCents, interval }. The price
  // and interval are what the disclosure and the consent name (the server's
  // after a plan-price-changed refusal).
  const [confirmPlan, setConfirmPlan] = useState(null);
  const [consent, setConsent] = useState(false); // renewal consent box; starts UNTICKED
  const checkoutContainerRef = useRef(null);
  const checkoutInstanceRef = useRef(null);

  const teardownCheckout = useCallback(() => {
    try { checkoutInstanceRef.current?.destroy(); } catch (e) { /* noop */ }
    checkoutInstanceRef.current = null;
  }, []);

  const reset = useCallback(() => {
    teardownCheckout();
    setEmbedded(null);
    setStep('plans');
    setBusyTier(null);
    setErrorMsg(null);
    setConfirmPlan(null);
    setConsent(false);
  }, [teardownCheckout]);

  const close = useCallback(() => {
    reset();
    if (onClose) onClose();
  }, [reset, onClose]);

  // A plan tap only opens the confirm step; nothing is created yet.
  const pickPlan = (t) => {
    setErrorMsg(null);
    setConsent(false);
    setConfirmPlan({ key: t.key, name: t.name, amountCents: t.priceCents, interval: 'month' });
    setStep('confirm');
  };

  const backToPlans = () => {
    setErrorMsg(null);
    setConsent(false);
    setConfirmPlan(null);
    setStep('plans');
  };

  // The only caller of createSubscriptionCheckout here: runs from "Continue to
  // payment", which stays disabled until the consent box is ticked.
  const subscribe = async () => {
    const plan = confirmPlan;
    if (!plan || !consent || busyTier) return;
    setBusyTier(plan.key);
    setErrorMsg(null);
    try {
      const fn = httpsCallable(getFunctions(), 'createSubscriptionCheckout');
      const res = await fn({
        tier: plan.key,
        interval: 'month',
        renewalConsent: { accepted: true, amountCents: plan.amountCents, interval: plan.interval, source: 'upsell_modal' },
      });
      const d = res.data || {};
      if (IS_WEB && d.clientSecret) {
        setEmbedded({
          clientSecret: d.clientSecret,
          publishableKey: d.publishableKey,
          disclosure: d.disclosure || planDisclosure(plan.name, plan.amountCents, plan.interval),
        });
        setStep('checkout');
      } else if (d.url && typeof window !== 'undefined') {
        window.location.href = d.url; // native/hosted fallback
      } else {
        throw new Error('Could not start checkout.');
      }
    } catch (err) {
      console.error('[UpgradeModal] subscribe failed:', err);
      const changed = planPriceChangedDetails(err);
      if (changed) {
        // The price moved: show the server's price and ask again, unticked.
        setConfirmPlan({ ...plan, amountCents: changed.amountCents, interval: changed.interval });
        setConsent(false);
        setErrorMsg(planErrorSentence(err));
      } else if (String(err?.message || '').includes('renewal-consent-required')) {
        setConsent(false);
        setErrorMsg(RENEWAL_CONSENT_REFRESH);
      } else {
        setErrorMsg(planErrorSentence(err, 'Could not start checkout. Please try again.'));
      }
    } finally {
      setBusyTier(null);
    }
  };

  // Mount the embedded checkout into the raw DOM node (RN Web renders real
  // divs; Stripe injects its iframe there). Same lifecycle as SubscriptionScreen.
  useEffect(() => {
    if (!IS_WEB || !embedded?.clientSecret || step !== 'checkout') return;
    let cancelled = false;
    (async () => {
      try {
        if (!embedded.publishableKey) throw new Error('Stripe publishable key is not configured.');
        const { loadStripe } = await import('@stripe/stripe-js');
        const stripe = await loadStripe(embedded.publishableKey);
        if (cancelled || !stripe) return;
        const instance = await stripe.createEmbeddedCheckoutPage({
          clientSecret: embedded.clientSecret,
          onComplete: () => {
            // Payment captured. The webhook flips subscriptionTier async — the
            // success step tells them it's activating; by the time they dismiss
            // and re-hit Create, it's essentially always landed.
            teardownCheckout();
            setEmbedded(null);
            setStep('success');
          },
        });
        if (cancelled) { try { instance.destroy(); } catch (e) { /* noop */ } return; }
        checkoutInstanceRef.current = instance;
        if (checkoutContainerRef.current) instance.mount(checkoutContainerRef.current);
      } catch (err) {
        console.error('[UpgradeModal] embedded checkout mount failed:', err);
        if (!cancelled) {
          setEmbedded(null);
          setStep('plans');
          setConfirmPlan(null);
          setConsent(false);
          setErrorMsg('Could not open checkout. Please try again.');
        }
      }
    })();
    return () => {
      cancelled = true;
      teardownCheckout();
    };
  }, [embedded, step, teardownCheckout]);

  if (!visible) return null;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={close}>
      <View style={styles.overlay}>
        <Pressable style={styles.scrim} onPress={step === 'checkout' || busyTier ? undefined : close} />
        <View style={styles.card}>

          {step === 'plans' && (
            <>
              <Text style={styles.title}>{headline}</Text>
              <Text style={styles.subtitle}>{subtitle}</Text>
              {errorMsg && <Text style={styles.error}>{errorMsg}</Text>}

              {TIERS.map((t) => (
                <Pressable
                  key={t.key}
                  style={[styles.tierRow, t.popular && styles.tierRowPopular]}
                  onPress={() => !busyTier && pickPlan(t)}
                  disabled={!!busyTier}
                >
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={styles.tierName}>{t.name}</Text>
                      {t.popular && <Text style={styles.popularTag}>MOST POPULAR</Text>}
                    </View>
                    {critsEnabled && <Text style={styles.tierCrits}>{t.crits} Crits every month</Text>}
                  </View>
                  <Text style={styles.tierPrice}>{`$${t.priceCents / 100}/mo`}</Text>
                </Pressable>
              ))}

              <View style={styles.footerRow}>
                <Pressable onPress={close} hitSlop={8}>
                  <Text style={styles.footerLink}>Not now</Text>
                </Pressable>
                {onSeeAllPlans && (
                  <Pressable onPress={() => { close(); onSeeAllPlans(); }} hitSlop={8}>
                    <Text style={styles.footerLink}>Yearly plans (4 months free) →</Text>
                  </Pressable>
                )}
              </View>
            </>
          )}

          {step === 'confirm' && confirmPlan && (
            <>
              <Text style={styles.title}>{`Start your ${confirmPlan.name} plan`}</Text>
              <Text style={styles.confirmPrice}>
                {`${formatPlanUsd(confirmPlan.amountCents)} a ${confirmPlan.interval === 'year' ? 'year' : 'month'}`}
              </Text>
              {/* The renewal terms, directly above the consent box (14.1). */}
              <Text style={styles.disclosure}>
                {planDisclosure(confirmPlan.name, confirmPlan.amountCents, confirmPlan.interval)}
              </Text>
              <ConsentCheckbox
                checked={consent}
                onToggle={setConsent}
                label={planConsentText(confirmPlan.interval, confirmPlan.amountCents)}
                disabled={!!busyTier}
              />
              {errorMsg && <Text style={styles.error}>{errorMsg}</Text>}
              <Pressable
                style={[styles.primaryBtn, (!consent || !!busyTier) && styles.primaryBtnDisabled]}
                onPress={subscribe}
                disabled={!consent || !!busyTier}
                accessibilityRole="button"
                accessibilityState={{ disabled: !consent }}
              >
                {busyTier
                  ? <ActivityIndicator color={C.bg} />
                  : <Text style={styles.primaryBtnText}>Continue to payment</Text>}
              </Pressable>
              <ConsentHint visible={!consent} />
              <View style={styles.footerRow}>
                <Pressable onPress={backToPlans} disabled={!!busyTier} hitSlop={8}>
                  <Text style={styles.footerLink}>Back to plans</Text>
                </Pressable>
                <Pressable onPress={close} disabled={!!busyTier} hitSlop={8}>
                  <Text style={styles.footerLink}>Not now</Text>
                </Pressable>
              </View>
            </>
          )}

          {step === 'checkout' && (
            <>
              <View style={styles.checkoutHeader}>
                <Text style={styles.title}>Start your plan</Text>
                <Pressable onPress={() => { teardownCheckout(); setEmbedded(null); backToPlans(); }} hitSlop={8}>
                  <Text style={styles.closeX}>✕</Text>
                </Pressable>
              </View>
              <ScrollView style={styles.checkoutScroll} showsVerticalScrollIndicator={false}>
                {/* The server's renewal disclosure stays above the payment form (14.1). */}
                {!!embedded?.disclosure && <Text style={styles.disclosure}>{embedded.disclosure}</Text>}
                {/* Stripe mounts its embedded checkout iframe into this div. */}
                <div ref={checkoutContainerRef} style={{ width: '100%', minHeight: 380 }} />
              </ScrollView>
            </>
          )}

          {step === 'success' && (
            <>
              <Text style={styles.successEmoji}>🎉</Text>
              <Text style={styles.title}>You're in!</Text>
              <Text style={styles.subtitle}>
                Plan activating{critsEnabled ? ' — your monthly Crits are on the way' : ''}. Hit
                Create Event again and launch the event you just built.
              </Text>
              <Pressable
                style={styles.primaryBtn}
                onPress={() => { const done = onUpgraded || close; reset(); done(); }}
              >
                <Text style={styles.primaryBtnText}>Back to my event</Text>
              </Pressable>
            </>
          )}

        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16 },
  scrim: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.8)' },
  card: {
    width: '100%', maxWidth: 460, backgroundColor: C.surface, borderRadius: THEME.radius.lg,
    borderWidth: 1, borderColor: C.borderStrong, padding: THEME.space.xl,
  },
  title: { color: C.brand, fontSize: 18, fontWeight: THEME.font.weightBold, textAlign: 'center' },
  subtitle: { color: C.textMuted, fontSize: 13, textAlign: 'center', marginTop: 8, lineHeight: 19 },
  error: { color: '#ff6b6b', fontSize: 12, textAlign: 'center', marginTop: 8 },
  tierRow: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: C.chip,
    borderWidth: 1, borderColor: C.border, borderRadius: THEME.radius.md,
    paddingVertical: 14, paddingHorizontal: 16, marginTop: 12,
  },
  tierRowPopular: { borderColor: C.brand },
  tierName: { color: C.text, fontSize: 15, fontWeight: THEME.font.weightBold },
  popularTag: {
    color: C.bg, backgroundColor: C.brand, fontSize: 9, fontWeight: THEME.font.weightHeavy,
    borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2, marginLeft: 8, overflow: 'hidden',
  },
  tierCrits: { color: C.textMuted, fontSize: 12, marginTop: 2 },
  tierPrice: { color: C.brand, fontSize: 15, fontWeight: THEME.font.weightBold, marginLeft: 12 },
  footerRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 16 },
  footerLink: { color: C.textMuted, fontSize: 13, textDecorationLine: 'underline' },
  checkoutHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  closeX: { color: C.textMuted, fontSize: 18, paddingHorizontal: 4 },
  checkoutScroll: { maxHeight: 520, marginTop: 12 },
  successEmoji: { fontSize: 40, textAlign: 'center', marginBottom: 8 },
  primaryBtn: {
    backgroundColor: C.brand, borderRadius: THEME.radius.md, paddingVertical: 13,
    alignItems: 'center', marginTop: 18,
  },
  primaryBtnText: { color: C.bg, fontSize: 15, fontWeight: THEME.font.weightHeavy },
  primaryBtnDisabled: { opacity: 0.45 },
  confirmPrice: { color: C.text, fontSize: 15, fontWeight: THEME.font.weightBold, textAlign: 'center', marginTop: 10 },
  disclosure: { color: C.textMuted, fontSize: 12.5, lineHeight: 18, marginTop: 12 },
});
